import { createRouter } from "@tanstack/react-router";
import {
  MutationCache,
  QueryCache,
  QueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import * as Sentry from "@sentry/react";

import { routeTree } from "../pages/router";
import { getErrorMessage } from "../utils/errors";
import { ApiError } from "../utils/fetch";

function isSignedOut(e: unknown): boolean {
  return e instanceof ApiError && e.status === 401 && e.body === "no active session";
}

// Expected client-class API failures (validation, max-spend, forbidden,
// conflict, ...) are surfaced to the user via toast but are not bugs, so they
// are kept out of Sentry. Only 5xx and non-API errors (network, parse, unknown)
// are captured as exceptions.
function shouldCapture(e: unknown): boolean {
  if (e instanceof ApiError) return e.status >= 500;
  return e instanceof Error;
}

function report(e: unknown) {
  if (isSignedOut(e)) return;
  toast.error(getErrorMessage(e));
  if (shouldCapture(e)) Sentry.captureException(e);
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (e, query) => {
      // Per-query onError still runs; only show a toast if the query did not opt out.
      if (query.meta?.silent !== true) report(e);
    },
  }),
  mutationCache: new MutationCache({
    onError: (e, _vars, _ctx, mutation) => {
      if (mutation.meta?.silent !== true && !mutation.options.onError) report(e);
    },
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

export const router = createRouter({ routeTree, context: { queryClient } });
